import React from 'react';
import { Box, Backdrop, Slide, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ProfileCard from './prospects/ProfileCard';
import logo from '../assets/rounded_logo.svg';

interface Prospect {
  name: string;
  email: string;
  phone_number?: string;
  age_range?: string;
  new_renter?: boolean;
  has_year_long_rental?: boolean;
  current_living_situation?: string;
  living_situation_explained?: string;
  student?: boolean;
  undergraduate?: boolean;
  currently_employed?: boolean;
  employer?: string;
  occupation?: string;
  income_verifiable?: boolean;
  has_current_job_for_year?: boolean;
  will_do_background_and_credit_check?: boolean;
  num_dogs?: number;
  num_cats?: number;
  lease_length_requested?: string;
  move_in_date_requested?: string;
  moving_reason?: string;
  number_of_applicants?: string;
  tour_preference?: string;
  short_intro?: string;
}

interface ProfileCardOverlayProps {
  prospect: Prospect;
  onClose: () => void;
}

function mapProspectToProfileData(prospect: Prospect) {
  const nameParts = prospect.name?.split(' ') || [];
  const firstName = nameParts[0] || '';
  const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';

  return {
    first_name: firstName,
    last_initial: lastInitial,
    profile_pic_url: null,
    short_intro: prospect.short_intro || '',
    prospectId: '',

    occupation: prospect.occupation || '',
    employer: prospect.employer || '',

    budget: '',
    bedrooms: '',
    bathrooms: '',
    desired_move_in: prospect.move_in_date_requested || '',
    lease_length: prospect.lease_length_requested || '',
    rental_type: undefined,
    tour_preference: prospect.tour_preference,
    occupants: prospect.number_of_applicants || '',
    neighborhoods: '',
    additionalInfo: '',

    age_range: prospect.age_range,
    student: prospect.student,
    undergraduate: prospect.undergraduate,
    current_living_situation: prospect.current_living_situation,
    moving_reason: prospect.moving_reason,

    will_do_background_and_credit_check: prospect.will_do_background_and_credit_check,
    has_current_job_for_year: prospect.has_current_job_for_year,
    has_year_long_rental: prospect.has_year_long_rental,
    income_verifiable: prospect.income_verifiable,

    housingVoucher: 'no',
    numDogs: String(prospect.num_dogs ?? 0),
    numCats: String(prospect.num_cats ?? 0),
  };
}

const ProfileCardOverlay: React.FC<ProfileCardOverlayProps> = ({ prospect, onClose }) => {
  const profileData = mapProspectToProfileData(prospect);

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: 1300, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            sx={{
              p: 0,
              borderRadius: 4,
              width: 600,
              maxWidth: '100%',
              maxHeight: '95vh',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 2,
                borderBottom: '1px solid #eee',
              }}
            >
              <Box>
                <img src={logo} alt="Heyy Property Logo" style={{ height: '48px', width: 'auto' }} />
              </Box>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 1, overflowY: 'auto', flex: 1 }}>
              <ProfileCard profileData={profileData} hideRequestTourButton />
            </Box>
          </Paper>
        </Box>
      </Slide>
    </Backdrop>
  );
};

export default ProfileCardOverlay;
